import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { emailRegValidator } from '../CommonFunction/validators';

import { makeStyles } from '@material-ui/core';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

import {
  checkMemberExistence,
  images as imageApi,
  requestPasswordReset,
} from '../Apis/apis';
import OnOffByScroll from '../Animation/onOffByScroll';
import StandardDialog from '../Dialog/standardDialog';
import various from '../General/various';
import Footer from '../Footer/footer';
import { ogpGenerator } from '../ogp';

const useStyles = makeStyles((theme) => ({
  container: {
    height: '500px',
  },
  formWrapper: {
    marginTop: '168px',
    '& > p': {
      textAlign: 'start',
    },
  },

  titles: {},
  inputs: {},
  error: {
    color: 'red',
    fontSize: '0.7em',
  },
  formcontainer: {
    marginTop: '32px',
    width: '100%',
    '& > form': {
      '& > div': {
        width: '100%',
        display: 'flex',
        margin: '0 auto',
        marginBottom: '32px',

        '& > div': {
          textAlign: 'start',
          display: 'flex',
          justifyContent: 'start',
          '& > img': {
            maxWidth: '300px',
            maxHeight: '300px',
            objectFit: 'contain',
          },
          '@media screen and (max-width:750px)': {
            width: '100%',
          },
        },

        '& $titles': {
          width: '5%',
          marginRight: '4px',
          '@media screen and (max-width:750px)': {
            width: '100%',
          },
          '& > span': {
            color: 'red',
          },
        },

        '& $inputs': {
          width: '80%',
          flexWrap: 'wrap',
          '@media screen and (max-width:750px)': {
            width: '100%',
          },
          '& > input': {
            width: '100%',
            height: '40px',
            padding: '8px',
            fontFamily: theme.typography.fontFamily,
            border: '0.5px solid grey',
            borderRadius: '0.5em',
            boxShadow: '0px 0px 15px 0px rgba(0,0,0,0.1) inset',
            '&:focus': {
              outline: `1.2px ${theme.palette.primary.main} outset`,
            },
          },
        },

        '@media screen and (max-width:750px)': {
          flexWrap: 'wrap',
        },
      },
    },
  },
  button: {
    width: '85% !important',
    margin: '32px 0px 0px 0px !important',
    justifyContent: 'end',
    '& > button': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      fontFamily: theme.typography.fontFamily,
      fontWeight: 'bold',
      borderRadius: '0.5em',
      border: 'none',
      padding: '4px 8px',
      backgroundColor: theme.palette.primary.main,
      boxShadow: '0px 0px 15px rgba(0,0,0,0.1)',
      color: '#fff',
      cursor: 'pointer',
      '&:disabled': {
        cursor: 'wait',
        opacity: 0.7,
      },
    },
  },
  spinner: {
    width: '14px',
    height: '14px',
    border: '2px solid rgba(255, 255, 255, 0.45)',
    borderTopColor: '#fff',
    borderRadius: '50%',
    animation: '$spin 0.8s linear infinite',
  },
  '@keyframes spin': {
    from: { transform: 'rotate(0deg)' },
    to: { transform: 'rotate(360deg)' },
  },
}));

const DIALOG_TEXT = {
  rejected: {
    title:
      'ご入力いただいたEメールアドレスではパスワードの再発行を承れません。',
    content: '',
    agree: 'OK',
  },
  failed: {
    title: 'パスワード再設定メールを送信できませんでした。',
    content: '時間をおいて、もう一度お試しください。',
    agree: 'OK',
  },
  succeeded: {
    title: 'ご登録のアドレスへ新規パスワード設定用リンクを送信しました',
    content: 'ご登録アドレスのメールボックスをご確認ください。',
    agree: 'OK',
  },
};

const ForgotPassword = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: 'onSubmit',
    defaultValues: {
      email: '',
    },
  });

  const [image, setImage] = useState();
  const [open, setOpen] = useState(false);
  const [dialogText, setDialogText] = useState({});
  const [submissionPhase, setSubmissionPhase] = useState('idle');
  const { executeRecaptcha } = useGoogleReCaptcha();

  const location = useLocation();
  const navigate = useNavigate();
  const classes = useStyles();

  // OGP設定
  const ogp = useCallback((props) => {
    ogpGenerator(props);
  }, []);

  useLayoutEffect(() => {
    if (image) {
      const ogpProps = {
        page: 'forgot',
        pageTitle: '刀禪OfficialWebSiteパスワード再設定ページ',
        path: location.pathname,
        imageUrl: image.OfficialImage_TouzenLogoBlack.url,
      };
      ogp(ogpProps);
    }
  }, [image, location, ogp]);
  // OGP設定ここまで

  useEffect(() => {
    imageApi(setImage);
  }, []);

  const handleClose = () => {
    setOpen(false);
    setSubmissionPhase('idle');
  };

  const handleDialogAgree = () => {
    if (submissionPhase === 'succeeded') {
      navigate(various.general.home);
      return;
    }
    handleClose();
  };

  const isSubmitting = ['checking', 'verifying', 'sending'].includes(
    submissionPhase
  );

  const onSubmit = async (data) => {
    if (isSubmitting) {
      return;
    }

    setOpen(false);
    setSubmissionPhase('checking');

    try {
      const exists = await checkMemberExistence({
        email: data.email,
        flag: 'forgot',
      });

      if (!exists) {
        setDialogText(DIALOG_TEXT.rejected);
        setSubmissionPhase('rejected');
        setOpen(true);
        return;
      }

      if (!executeRecaptcha) {
        throw new Error('reCAPTCHA is not ready');
      }

      setSubmissionPhase('verifying');
      const reCaptchaToken = await executeRecaptcha('contact');
      if (!reCaptchaToken) {
        throw new Error('reCAPTCHA token is unavailable');
      }

      setSubmissionPhase('sending');
      await requestPasswordReset({
        ...data,
        g_recaptcha_response: reCaptchaToken,
        protocol: window.location.protocol,
        host: window.location.host,
        domain: document.domain,
      });

      setDialogText(DIALOG_TEXT.succeeded);
      setSubmissionPhase('succeeded');
      setOpen(true);
    } catch (error) {
      setDialogText(DIALOG_TEXT.failed);
      setSubmissionPhase('failed');
      setOpen(true);
    }
  };

  useEffect(() => {
    const reloadKey = 'hasReloaded';
    if (!sessionStorage.getItem(reloadKey)) {
      sessionStorage.setItem(reloadKey, 'true');
      window.location.reload();
    }
  }, []);

  return (
    <>
      <div className={classes.container}>
        <div>
          <OnOffByScroll place="General" />
        </div>
        <div className={classes.formWrapper}>
          <p>ご登録されているメールアドレスを送信してください。</p>
          <div className={classes.formcontainer}>
            <form
              method="POST"
              encType="multipart/form-data"
              onSubmit={handleSubmit(onSubmit)}
            >
              <div>
                <div className={classes.titles}>
                  <label htmlFor="">{''}</label>
                  <span> *</span>
                </div>
                <div className={classes.inputs}>
                  <input
                    id="email"
                    type="email"
                    {...register('email', {
                      required: { value: true, message: '入力必須の項目です' },
                      validate: {
                        message: (v) =>
                          emailRegValidator(v)
                            ? null
                            : `※正しいEメールの形式で入力してください`,
                      },
                    })}
                  />
                  {errors.email?.message && (
                    <div className={classes.error}>{errors.email.message}</div>
                  )}
                </div>
              </div>
              <div className={classes.button}>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  aria-busy={isSubmitting}
                >
                  {isSubmitting && (
                    <span className={classes.spinner} aria-hidden="true" />
                  )}
                  {submissionPhase === 'checking'
                    ? '登録確認中…'
                    : submissionPhase === 'verifying'
                    ? '認証確認中…'
                    : submissionPhase === 'sending'
                    ? '送信中…'
                    : '送信'}
                </button>
              </div>
            </form>
          </div>
        </div>
        <StandardDialog
          open={open}
          handleClose={handleClose}
          text={{
            title: dialogText.title,
            content: dialogText.content,
            agree: dialogText.agree,
            disagree: null,
          }}
          func={{ agreeFunc: handleDialogAgree, disagreeFunc: null }}
        />
      </div>
      <Footer />
    </>
  );
};
export default ForgotPassword;
